import { useEffect, useState } from 'react'

/** For the handful of places where the thing that changes is content rather
 *  than style — a placeholder is text, and text has no media query. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia?.(query).matches ?? false,
  )

  useEffect(() => {
    const list = window.matchMedia?.(query)
    if (!list) return
    setMatches(list.matches)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** The app's breakpoints, named once in JS so a component never hand-writes a
 *  width that can drift from the `md:` and `lg:` it is paired with in the
 *  markup. These are Tailwind's own defaults; `tokens.css` adds `consult` and
 *  `wide` above them, which are style-only and have no business here. */
const BREAKPOINTS = { sm: '40rem', md: '48rem', lg: '64rem' } as const

/**
 * True from this breakpoint up — `useMinWidth('md')` is the `md:` prefix.
 *
 * For the places where the two postures are different *markup* rather than
 * different style: a record is a table on a desktop and a list of cards on a
 * phone, and no amount of CSS turns one into the other without throwing away
 * the semantics that make either readable.
 */
export function useMinWidth(breakpoint: keyof typeof BREAKPOINTS): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[breakpoint]})`)
}
